import React, {
  useMemo,
  useRef,
  useCallback,
  useState,
  useEffect,
} from "react";
import { Modal, Button, Space, message } from "antd";
import { useTranslation } from "react-i18next";
import type { FormInstance } from "antd/es/form";
import DynamicForm from "@/components/DynamicForm";
import ProcessTimeline from "@/components/ProcessTimeline";
import type { ProcessTimelineItem } from "@/components/ProcessTimeline";
import { getPersonnelInfo } from "@/pages/supplier/SupplierException/utils/fillFormFields";
import { getPersonnelFormItems, FACTORY_OPTIONS } from "./config/formConfig";
import {
  getMaterialInfoByCode,
  getSupplierInfo,
  generateProcessCode,
  startMeetingApproval,
  getMaterialExceptionInfoById,
  getHistoryByInsId,
} from "@/apis/supplier";
import { passFlow } from "@/apis/flow.api";
import type { PersonnelInfo } from "./types";
import dayjs from "dayjs";
import styles from "./index.module.less";

// ==================== 类型定义 ====================
interface RegisterMaterialExceptionModalProps {
  visible: boolean;
  editId?: number | string; // 编辑模式的ID
  readOnly?: boolean; // 查看模式：所有字段禁用编辑
  businessStatus?: number;
  onSubmit?: (data: any) => void;
  onClose?: () => void;
}

// ==================== 渲染函数 ====================

/**
 * 获取物料异常登记Modal标题
 */
const getModalTitle = (
  t: any,
  isEditMode: boolean = false,
  processCode: string = "",
  readOnly: boolean = false,
) => {
  let title = "";
  if (readOnly) {
    title = t("查看物料异常");
  } else {
    title = isEditMode ? t("编辑物料异常") : t("登记物料异常标题");
  }

  return (
    <div className={styles.titleContainer}>
      <span>{title}</span>
      <div className={styles.titleDivider} />
      <span className={styles.titleProcessCode}>
        {t("单号")}：{processCode}
      </span>
    </div>
  );
};

// ==================== 主组件 ====================
const RegisterMaterialExceptionModal: React.FC<
  RegisterMaterialExceptionModalProps
> = ({
  visible,
  editId,
  readOnly = false,
  businessStatus,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation("materialExceptionLevel");
  const formRef = useRef<FormInstance>(null);
  const [loading, setLoading] = useState(false);
  const [processCode, setProcessCode] = useState<string>("");
  const [taskId, setTaskId] = useState<number | null>(null);
  const [editDataId, setEditDataId] = useState<number | null>(null);
  const [timelineData, setTimelineData] = useState<ProcessTimelineItem[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<any[]>([]);
  const [materialCategoryOptions, setMaterialCategoryOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [materialTwoCategoryOptions, setMaterialTwoCategoryOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const isEditMode = !!editId; // 直接计算，无需状态
  const [personnelInfo, setPersonnelInfo] = useState<PersonnelInfo | null>(
    null,
  );

  // 物料编号搜索状态
  const [materialOptions, setMaterialOptions] = useState<any[]>([]);
  const [materialDataMap, setMaterialDataMap] = useState<Map<string, any>>(
    new Map(),
  );
  const [materialLoading, setMaterialLoading] = useState(false);

  // 物料进仓批号搜索状态
  const [batchOptions, setBatchOptions] = useState<any[]>([]);
  const [batchDataMap, setBatchDataMap] = useState<Map<string, any>>(new Map());
  const [batchLoading, setBatchLoading] = useState(false);

  // 物料编号搜索处理
  const handleMaterialCodeSearch = useCallback(async (value: string) => {
    if (!value || value.trim().length === 0) {
      setMaterialOptions([]);
      return;
    }

    setMaterialLoading(true);
    try {
      const response = await getMaterialInfoByCode(value);
      console.log("API 响应:", response);
      const materialData = response?.data?.data;
      console.log("materialData:", materialData);

      if (materialData) {
        const dataArray = Array.isArray(materialData)
          ? materialData
          : [materialData];

        const options = dataArray.map((item: any) => ({
          label: `${item.materialCode}`,
          value: item.materialCode,
        }));

        const newMap = new Map();
        dataArray.forEach((item: any) => {
          newMap.set(item.materialCode, item);
        });
        setMaterialDataMap(newMap);
        setMaterialOptions(options);
      } else {
        console.warn("materialData 为空:", response);
        setMaterialOptions([]);
      }
    } catch (error) {
      setMaterialOptions([]);
    } finally {
      setMaterialLoading(false);
    }
  }, []);

  const handleMaterialSelect = useCallback(
    (value: string) => {
      if (!formRef.current) return;

      const materialData = materialDataMap.get(value);

      if (materialData) {
        formRef.current.setFieldsValue({
          materialCode: value,
          materialName: materialData.materialName || "",
          supplierUserCode: materialData.supplierCode || "",
          supplierUserName: materialData.supplierName || "",
          supplierUsUserName: materialData.supplierEnglishName || "",
          materialCategoryValue: materialData.materialCategoryId || "",
          materialTwoCategoryValue: materialData.materialTwoCategoryId || "",
          productionBatch: materialData.productionBatch || "",
          poNumber: materialData.poNumber || "",
          warehouseEntryNumber: materialData.warehouseEntryNumber || "",
        });
      }

      setMaterialOptions([]);
    },
    [materialDataMap],
  );

  // 物料进仓批号搜索处理
  const handleWarehouseEntryNumberSearch = useCallback(
    async (value: string) => {
      if (!value || value.trim().length === 0) {
        setBatchOptions([]);
        return;
      }

      setBatchLoading(true);
      try {
        if (!formRef.current) return;

        const formValues = formRef.current.getFieldsValue();
        const fdFactory = formValues?.factoryValue;
        const materialCode = formValues?.materialCode;

        if (!fdFactory || !materialCode) {
          message.warning(t("请先选择公司和物料编号"));
          setBatchLoading(false);
          return;
        }
        const response = await getSupplierInfo(fdFactory, materialCode, value);
        const batchData = response?.data?.data;
        console.log("batchData:", batchData);

        if (batchData) {
          const dataArray = Array.isArray(batchData) ? batchData : [batchData];

          const options = dataArray.map((item: any) => ({
            label: item.batchNumber || value,
            value: item.batchNumber || value,
          }));

          const newMap = new Map();
          dataArray.forEach((item: any) => {
            newMap.set(item.batchNumber || value, item);
          });
          setBatchDataMap(newMap);
          setBatchOptions(options);
        } else {
          console.warn("batchData 为空:", response);
          setBatchOptions([]);
        }
      } catch (error) {
        setBatchOptions([]);
      } finally {
        setBatchLoading(false);
      }
    },
    [t],
  );

  const onBatchDataUpdate = useCallback(
    (data: {
      materialCategoryOptions: Array<{ label: string; value: string }>;
      materialTwoCategoryOptions: Array<{ label: string; value: string }>;
      manufacturerOptions: any[];
    }) => {
      setMaterialCategoryOptions(data.materialCategoryOptions);
      setMaterialTwoCategoryOptions(data.materialTwoCategoryOptions);
      setManufacturerOptions(data.manufacturerOptions);
    },
    [],
  );

  const handleBatchNumberSelect = useCallback(
    (value: string) => {
      if (!formRef.current) return;
      const batchData = batchDataMap.get(value);
      if (batchData) {
        const manufacturerOptions = (batchData.manufacturerList || []).map(
          (item: any) => ({
            label: item.supplierName || "",
            value: item.supplierName || "",
          }),
        );

        const materialCategoryOptions = batchData.materialCategoryName
          ? [
              {
                label: batchData.materialCategoryName,
                value: batchData.materialCategoryValue,
              },
            ]
          : [];
        const materialTwoCategoryOptions = batchData.materialTwoCategoryName
          ? [
              {
                label: batchData.materialTwoCategoryName,
                value: batchData.materialTwoCategoryValue,
              },
            ]
          : [];

        const fieldsToUpdate = {
          warehouseEntryNumber: value,
          supplierUserCode: batchData.supplierCode || "",
          supplierUserId: batchData.supplierUserId || "",
          supplierUserName: batchData.supplierUserName || "",
          supplierUsUserName: batchData.supplierUsUserName || "",
          materialCategoryValue: batchData.materialCategoryValue || "",
          materialTwoCategoryValue: batchData.materialTwoCategoryValue || "",
          productionBatch: batchData.productionBatch || "",
          poNumber: batchData.poNumber || "",
        };
        formRef.current.setFieldsValue(fieldsToUpdate);
        onBatchDataUpdate?.({
          materialCategoryOptions,
          materialTwoCategoryOptions,
          manufacturerOptions,
        });
      } else {
      }

      setBatchOptions([]);
    },
    [batchDataMap, onBatchDataUpdate],
  );

  // 模态框打开时加载数据
  useEffect(() => {
    if (visible) {
      console.log(
        "🔍 RegisterMaterialExceptionModal - businessStatus:",
        businessStatus,
      );
      const info = getPersonnelInfo();
      setPersonnelInfo(info);

      if (isEditMode && editId) {
        const fetchEditData = async () => {
          try {
            setLoading(true);
            const response = await getMaterialExceptionInfoById(editId);
            console.log("编辑数据 API 响应:", response);
            const editData = response?.data?.data;

            if (editData && formRef.current) {
              setProcessCode(editData.processCode || "");
              setTaskId(editData.taskId || null);
              setEditDataId(editData.id || null);

              // ==================== 获取流程动态 ====================
              if (editData.insId && readOnly) {
                getHistoryByInsId(editData.insId)
                  .then((historyResponse) => {
                    if (
                      historyResponse?.data?.code === 200 &&
                      Array.isArray(historyResponse?.data?.data)
                    ) {
                      const timeline = historyResponse.data.data.map(
                        (item: any) => ({
                          approver: item.approver,
                          flowStatus: item.flowStatus,
                          postName: item.postName,
                          createTime: item.createTime,
                          message: item.message,
                          avatarUrl:
                            item.avatarUrl ||
                            "https://gw.alipayobjects.com/zos/antfincdn/efFD%24IOql2/weixintupian_20170331104822.jpg",
                        }),
                      );
                      setTimelineData(timeline);
                    }
                  })
                  .catch((historyError) => {
                    console.error("获取历史记录失败:", historyError);
                  });
              }

              let supplierLabelFileIds: any[] = [];
              let relatedProofFileIds: any[] = [];
              let solutionFiles: any[] = [];

              if (
                editData.srmSupplierFlowBusinessFiles &&
                Array.isArray(editData.srmSupplierFlowBusinessFiles)
              ) {
                editData.srmSupplierFlowBusinessFiles.forEach(
                  (file: any, index: number) => {
                    const fileType =
                      typeof file.fileType === "string"
                        ? parseInt(file.fileType, 10)
                        : file.fileType;

                    const fileObj = {
                      uid: file.fileId || `file-${Math.random()}`,
                      name:
                        file.fileName ||
                        file.fileUrl?.split("/").pop() ||
                        "文件",
                      status: "done" as const,
                      url: file.fileUrl,
                      fileId: file.fileId,
                    };

                    if (fileType === 6 || file.fileType === "6") {
                      supplierLabelFileIds.push(fileObj);
                    } else if (fileType === 7 || file.fileType === "7") {
                      relatedProofFileIds.push(fileObj);
                    } else if (file.fileType === "solutionFile") {
                      solutionFiles.push({
                        id: file.fileId || file.id,
                        fileName: file.fileName,
                        fileUrl: file.fileUrl,
                        remark: file.remark,
                        fileType: file.fileType,
                      });
                    } else {
                    }
                  },
                );
              }

              const formattedData = {
                ...editData,
                srmSupplierFlowBusinessFiles:
                  solutionFiles.length > 0 ? solutionFiles : undefined,
                supplierLabelFileIds:
                  supplierLabelFileIds.length > 0
                    ? supplierLabelFileIds
                    : undefined,
                relatedProofFileIds:
                  relatedProofFileIds.length > 0
                    ? relatedProofFileIds
                    : undefined,
                discoveryDate: editData.discoveryDate
                  ? dayjs(editData.discoveryDate)
                  : undefined,
                expectedResolutionDate: editData.expectedResolutionDate
                  ? dayjs(editData.expectedResolutionDate)
                  : undefined,
                supplierOpinion: editData.decisionRefundOrReplace || undefined,
                ...(businessStatus === 5 && {
                  remarks: editData.remarks || undefined,
                  approvalOpinion: editData.approvalOpinion || undefined,
                  approvalComments: editData.approvalComments || undefined,
                }),
              };

              console.log("=== 设置表单值前 ===");
              console.log("formattedData:", formattedData);
              formRef.current.setFieldsValue(formattedData);
              if (editData.materialCategoryName) {
                setMaterialCategoryOptions([
                  {
                    label: editData.materialCategoryName,
                    value: editData.materialCategoryValue || "",
                  },
                ]);
              }
              if (editData.materialTwoCategoryName) {
                setMaterialTwoCategoryOptions([
                  {
                    label: editData.materialTwoCategoryName,
                    value: editData.materialTwoCategoryValue || "",
                  },
                ]);
              }
            }
          } catch (error) {
            console.error("加载编辑数据失败:", error);
          } finally {
            setLoading(false);
          }
        };

        fetchEditData();
      } else {
        if (formRef.current) {
          formRef.current.resetFields();
          setMaterialCategoryOptions([]);
          setMaterialTwoCategoryOptions([]);
          setManufacturerOptions([]);
        }

        const fetchProcessCode = async () => {
          try {
            setLoading(true);
            const response = await generateProcessCode(
              "SUPPLIER_MATERIAL_ABNORMAL_REGISTER",
            );
            let code = response?.data?.data;

            // 如果返回的数据包含"加载中"等文本，需要提取实际的单号
            if (typeof code === "string") {
              // 移除"单号："前缀和"加载中..."等文本，只保留实际的单号
              code = code.replace(/^[^A-Z0-9]*/, "").trim();
              if (code) {
                setProcessCode(code);
              } else {
                setProcessCode(t("生成失败"));
              }
            } else {
              setProcessCode(t("生成失败"));
            }
          } catch (error) {
            console.error("生成单号失败:", error);
            setProcessCode(t("生成失败"));
          } finally {
            setLoading(false);
          }
        };

        fetchProcessCode();
      }
    } else {
      setProcessCode("");
      setTaskId(null);
      setEditDataId(null);
      setTimelineData([]);
    }
  }, [visible, editId, businessStatus, isEditMode, readOnly, t]);

  // 创建动态formConfig
  const formConfig = useMemo(
    () => ({
      onValuesChange: (changedValues: any, _allValues: any) => {
        if (
          changedValues.materialCode &&
          typeof changedValues.materialCode === "string"
        ) {
          const selectedValue = changedValues.materialCode;
          const isSelected = materialOptions.some(
            (opt: any) => opt.value === selectedValue,
          );
          if (isSelected) {
            handleMaterialSelect(selectedValue);
          }
        }

        if (
          changedValues.warehouseEntryNumber &&
          typeof changedValues.warehouseEntryNumber === "string"
        ) {
          const selectedBatchValue = changedValues.warehouseEntryNumber;
          const isSelected = batchOptions.some(
            (opt: any) => opt.value === selectedBatchValue,
          );
          if (isSelected) {
            handleBatchNumberSelect(selectedBatchValue);
          }
        }

        if (changedValues.requiredInvestigationValue !== undefined) {
          if (formRef.current) {
            formRef.current.setFieldValue(
              "requiredInvestigationName",
              changedValues.requiredInvestigationValue,
            );
          }
        }
      },
    }),
    [
      materialOptions,
      handleMaterialSelect,
      batchOptions,
      handleBatchNumberSelect,
    ],
  );

  // 获取表单项
  const formItems = useMemo(() => {
    const items = getPersonnelFormItems(
      personnelInfo,
      handleMaterialCodeSearch,
      undefined,
      handleWarehouseEntryNumberSearch,
      businessStatus,
      t,
      readOnly,
    );

    return items.map((item: any) => {
      if (item.name === "factoryValue" && item.widget) {
        console.log("🔍 factoryValue item before:", item);
        const newItem = {
          ...item,
          widget: {
            ...item.widget,
            options: FACTORY_OPTIONS,
          },
        };
        console.log("🔍 factoryValue item after:", newItem);
        return newItem;
      }
      if (item.name === "materialCode" && item.widget) {
        return {
          ...item,
          widget: {
            ...item.widget,
            options: materialOptions,
            loading: materialLoading,
          },
        };
      }
      if (item.name === "warehouseEntryNumber" && item.widget) {
        return {
          ...item,
          widget: {
            ...item.widget,
            options: batchOptions,
            loading: batchLoading,
          },
        };
      }
      if (item.name === "manufacturerName" && item.widget) {
        return {
          ...item,
          widget: {
            ...item.widget,
            options: manufacturerOptions,
          },
        };
      }
      if (item.name === "materialCategoryValue" && item.widget) {
        return {
          ...item,
          widget: {
            ...item.widget,
            options: materialCategoryOptions,
          },
        };
      }
      if (item.name === "materialTwoCategoryValue" && item.widget) {
        return {
          ...item,
          widget: {
            ...item.widget,
            options: materialTwoCategoryOptions,
          },
        };
      }
      return item;
    });
  }, [
    personnelInfo,
    handleMaterialCodeSearch,
    materialOptions,
    materialLoading,
    batchOptions,
    batchLoading,
    manufacturerOptions,
    materialCategoryOptions,
    materialTwoCategoryOptions,
    handleWarehouseEntryNumberSearch,
    businessStatus,
    readOnly,
    t,
  ]);

  const handleSubmit = useCallback(async () => {
    try {
      if (formRef.current) {
        setLoading(true);
        const formValue = formRef.current.getFieldsValue();

        if (businessStatus === 1 && taskId) {
          const passData = {
            flowCode: "SUPPLIER_MATERIAL_ABNORMAL_REGISTER" as const,
            skipType: "PASS" as const,
            message: "",
            variable: {},
            srmMaterialAbnormalRegisterDTO: {
              id: editDataId,
              processingStatus: 2,
              decisionRefundOrReplace: formValue.supplierOpinion || "",
            },
          };
          await passFlow(taskId, passData);
          message.success(t("已登记"));
          onClose?.();
        } else if (businessStatus === 2 && taskId) {
          const passData = {
            skipType: "PASS" as const,
            flowCode: "SUPPLIER_MATERIAL_ABNORMAL_REGISTER" as const,
            message: "",
            variable: {},
            srmMaterialAbnormalRegisterDTO: {
              id: editDataId,
              processingStatus: 3,
              decisionRefundOrReplace: formValue.supplierOpinion || "",
              description: formValue.description || "",
              requiredInvestigationName:
                formValue.requiredInvestigationName || "",
              requiredInvestigationValue:
                formValue.requiredInvestigationValue || "",
              expectedResolutionDate: formValue.expectedResolutionDate
                ? dayjs.isDayjs(formValue.expectedResolutionDate)
                  ? formValue.expectedResolutionDate.format("YYYY-MM-DD")
                  : formValue.expectedResolutionDate
                : "",
            },
          };

          await passFlow(taskId, passData);
          message.success(t("已登记"));
          onClose?.();
        } else if (businessStatus === 5 && taskId) {
          const approvalOpinion = formValue.approvalOpinion;
          const skipType =
            approvalOpinion === "agree"
              ? ("PASS" as const)
              : ("REJECT" as const);
          const processingStatus = approvalOpinion === "agree" ? 5 : 6;

          const passData = {
            flowCode: "SUPPLIER_MATERIAL_ABNORMAL_REGISTER" as const,
            skipType,
            message: formValue.approvalComments || "",
            variable: {},
            srmMaterialAbnormalRegisterDTO: {
              id: editDataId,
              processingStatus,
            },
          };
          await passFlow(taskId, passData);
          message.success(t("已登记"));
          onClose?.();
        } else {
          const factoryOption = FACTORY_OPTIONS.find(
            (option) => option.value === formValue.factoryValue,
          );
          const factoryName = factoryOption?.label || "";

          const mergedData = {
            ...formValue,
            processCode,
            processingStatus: 1,
            factoryName,
            materialCategoryName: materialCategoryOptions[0]?.label || "",
            materialTwoCategoryName: materialTwoCategoryOptions[0]?.label || "",
            decisionRefundOrReplace: formValue.supplierOpinion || undefined,
            fileIds: [
              ...(formValue.supplierLabelFileIds || []),
              ...(formValue.relatedProofFileIds || []),
            ].map((file: any) => file.fileId || file.id),
          };

          if (
            mergedData.discoveryDate &&
            dayjs.isDayjs(mergedData.discoveryDate)
          ) {
            mergedData.discoveryDate =
              mergedData.discoveryDate.format("YYYY-MM-DD");
          }

          if (
            mergedData.expectedResolutionDate &&
            dayjs.isDayjs(mergedData.expectedResolutionDate)
          ) {
            mergedData.expectedResolutionDate =
              mergedData.expectedResolutionDate.format("YYYY-MM-DD");
          }

          if (isEditMode && taskId) {
            const passData = {
              flowCode: "SUPPLIER_MATERIAL_ABNORMAL_REGISTER" as const,
              formNo: processCode,
              variable: {
                supplierUserId: formValue.supplierUserId,
              },
              srmMaterialAbnormalRegisterDTO: {
                ...mergedData,
                id: editDataId,
              },
            };

            await passFlow(taskId, passData);
            message.success(t("已登记"));
            onClose?.();
          } else {
            const newSubmitData = {
              flowStatus: 1,
              flowCode: "SUPPLIER_MATERIAL_ABNORMAL_REGISTER" as const,
              formNo: `${processCode}`,
              variable: {
                supplierUserId: formValue.supplierUserId,
              },
              srmMaterialAbnormalRegisterDTO: mergedData,
            };

            await startMeetingApproval(newSubmitData);
            message.success(t("已登记"));
            onClose?.();
          }
        }
      }
    } catch (error) {
      console.error("提交失败:", error);
    } finally {
      setLoading(false);
    }
  }, [
    onClose,
    materialCategoryOptions,
    materialTwoCategoryOptions,
    processCode,
    isEditMode,
    taskId,
    editDataId,
    businessStatus,
    t,
  ]);

  const handleSaveTemporarily = useCallback(async () => {
    try {
      if (formRef.current) {
        setLoading(true);
        const formValue = formRef.current.getFieldsValue();

        const factoryOption = FACTORY_OPTIONS.find(
          (option) => option.value === formValue.factoryValue,
        );
        const factoryName = factoryOption?.label || "";

        const mergedData = {
          ...formValue,
          processCode,
          processingStatus: 0,
          factoryName,
          supplierUserId: formValue.supplierUserId,
          materialCategoryName: materialCategoryOptions[0]?.label || "",
          materialTwoCategoryName: materialTwoCategoryOptions[0]?.label || "",
          fileIds: [
            ...(formValue.supplierLabelFileIds || []),
            ...(formValue.relatedProofFileIds || []),
          ].map((file: any) => file.fileId || file.id),
        };

        if (
          mergedData.discoveryDate &&
          dayjs.isDayjs(mergedData.discoveryDate)
        ) {
          mergedData.discoveryDate =
            mergedData.discoveryDate.format("YYYY-MM-DD");
        }

        if (
          mergedData.expectedResolutionDate &&
          dayjs.isDayjs(mergedData.expectedResolutionDate)
        ) {
          mergedData.expectedResolutionDate =
            mergedData.expectedResolutionDate.format("YYYY-MM-DD");
        }

        if (isEditMode && taskId) {
          const passData = {
            flowStatus: 0,
            flowCode: "SUPPLIER_MATERIAL_ABNORMAL_REGISTER" as const,
            formNo: processCode,
            variable: {
              supplierUserId: formValue.supplierUserId,
            },
            srmMaterialAbnormalRegisterDTO: {
              ...mergedData,
              id: editDataId,
            },
          };

          await passFlow(taskId, passData);
          message.success(t("已暂存"));
        } else {
          const newSubmitData = {
            flowStatus: 0,
            flowCode: "SUPPLIER_MATERIAL_ABNORMAL_REGISTER" as const,
            formNo: `${processCode}`,
            variable: {
              supplierUserId: formValue.supplierUserId,
            },
            srmMaterialAbnormalRegisterDTO: mergedData,
          };
          await startMeetingApproval(newSubmitData);
          message.success(t("已暂存"));
        }
      }
    } catch (error) {
      console.error("暂存失败:", error);
      message.error(t("暂存失败"));
    } finally {
      setLoading(false);
      onClose?.();
    }
  }, [
    onClose,
    materialCategoryOptions,
    materialTwoCategoryOptions,
    processCode,
    isEditMode,
    taskId,
    editDataId,
    t,
  ]);

  const handleModalClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleNotifySupplier = useCallback(async () => {
    try {
      if (formRef.current && taskId) {
        setLoading(true);
        const notifyData = {
          flowCode: "SUPPLIER_MATERIAL_ABNORMAL_REGISTER" as const,
          skipType: "PASS" as const,
          message: "",
          variable: {},
          srmMaterialAbnormalRegisterDTO: {
            id: editDataId,
            processingStatus: 4,
          },
        };
        await passFlow(taskId, notifyData);
        message.success(t("已通知供应商"));
        onClose?.();
      }
    } catch (error) {
      console.error("通知供应商失败:", error);
      message.error(t("通知供应商失败"));
    } finally {
      setLoading(false);
    }
  }, [onClose, taskId, editDataId, t]);

  return (
    <Modal
      title={getModalTitle(
        t,
        isEditMode,
        processCode,
        readOnly || businessStatus === 5,
      )}
      open={visible}
      width={1300}
      style={{ top: 20 }}
      onCancel={handleModalClose}
      className={styles.materialExceptionModal}
      footer={
        <div className={styles.modalFooter}>
          {businessStatus === 1 || businessStatus === 2 ? (
            <Space>
              <Button onClick={handleModalClose}>{t("common:关闭")}</Button>
              <Button type="primary" loading={loading} onClick={handleSubmit}>
                {t("common:提交")}
              </Button>
            </Space>
          ) : businessStatus === 3 ? (
            <Space>
              <Button onClick={handleModalClose}>{t("common:关闭")}</Button>
              <Button
                type="primary"
                loading={loading}
                onClick={handleNotifySupplier}
              >
                {t("通知供应商")}
              </Button>
            </Space>
          ) : businessStatus === 5 || readOnly ? (
            <Space>
              <Button onClick={handleModalClose}>{t("common:关闭")}</Button>
              <Button type="primary" loading={loading} onClick={handleSubmit}>
                {t("common:提交")}
              </Button>
            </Space>
          ) : (
            <Space>
              <Button loading={loading} onClick={handleSaveTemporarily}>
                {t("common:暂存")}
              </Button>
              <Button type="primary" loading={loading} onClick={handleSubmit}>
                {t("common:提交")}
              </Button>
            </Space>
          )}
        </div>
      }
    >
      <div className={styles.modalBody}>
        <div style={{ display: "flex", gap: "20px" }}>
          {/* 左侧表单区域 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <DynamicForm
              ref={formRef}
              formItem={formItems}
              formValue={personnelInfo || {}}
              formConfig={formConfig}
              readOnly={readOnly || businessStatus === 5}
            />
          </div>

          {/* 右侧流程动态区域 - 当只读模式且有历史数据时显示 */}
          {readOnly && timelineData.length > 0 && (
            <div style={{ width: "320px", flexShrink: 0 }}>
              <div
                style={{
                  marginBottom: "12px",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#1d2129",
                }}
              >
                {t("流程动态")}
              </div>
              <ProcessTimeline items={timelineData} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RegisterMaterialExceptionModal;
